import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '@/shared/contexts/I18nProvider';

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const getScrollProgress = () => {
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

  if (maxScroll <= 0) {
    return 0;
  }

  return Math.min(Math.max(window.scrollY / maxScroll, 0), 1);
};

const BackToTopButton = () => {
  const { content } = useI18n();
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let frameId: number | null = null;

    const updateProgress = () => {
      frameId = null;
      setProgress(getScrollProgress());
      setIsVisible(window.scrollY > 240);
    };

    const queueUpdate = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener('scroll', queueUpdate);
      window.removeEventListener('resize', queueUpdate);
    };
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setProgress(getScrollProgress());
      setIsVisible(window.scrollY > 240);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname]);

  const strokeDashoffset = CIRCUMFERENCE * (1 - progress);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-5 right-5 z-50 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border border-border-primary bg-background-card/85 text-accent-primary shadow-lg shadow-shadow backdrop-blur-xl transition-colors hover:bg-background-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
          aria-label={content.common.backToTop}
          title={content.common.backToTop}
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.96 }}
          transition={{ duration: 0.2 }}
        >
          <svg
            viewBox="0 0 56 56"
            className="absolute inset-0 h-full w-full -rotate-90"
            aria-hidden="true"
          >
            <circle
              cx="28"
              cy="28"
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeOpacity="0.18"
            />
            <circle
              cx="28"
              cy="28"
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
            />
          </svg>
          <ArrowUp className="relative z-10 h-5 w-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default BackToTopButton;
